import React, { useEffect, useState } from "react";
import { useStores } from "../../../use-stores";
import {
  Box,
  CircularProgress,
  Container,
  Grid2,
  Paper,
  useTheme,
} from "@mui/material";
import { observer } from "mobx-react";

import { SortType } from "../../../models/SortType";
import { useNotification } from "../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";

import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import CustomTypography from "../../components/common/form/CustomTypography";
import { VoucherRequestsStore } from "../../../stores/VoucherRequestsStore";
import VouchersRequestsTable from "../../components/vouchers/VouchersRequestsTable";

const voucherRequestsStore = new VoucherRequestsStore();

const VouchersRequests: React.FC = () => {
  const theme = useTheme();
  const { userStore } = useStores();

  const { showError } = useNotification();

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchVouchersRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchVouchersRequests = async () => {
    if (userStore.hasPermission([EmployeeRoles.VIEW_VOUCHER_REQUESTS])) {
      setIsLoading(true);
      voucherRequestsStore
        .getPullVouchersRequests()
        .catch((err) =>
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later."
          )
        )
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const handleCheckStatus = async (id: number, requestId: string) => {
    if (userStore.hasPermission([EmployeeRoles.VIEW_VOUCHER_REQUESTS])) {
      setIsLoading(true);
      voucherRequestsStore
        .checkPullVoucherRequestStatus(id, requestId)
        .then(() => {
          setIsLoading(false);
          fetchVouchersRequests();
        })
        .catch((err) => {
          setIsLoading(false);
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later."
          );
        });
    }
  };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={{ px: 2 }}>
      {!userStore.hasPermission([EmployeeRoles.VIEW_VOUCHER_REQUESTS]) && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Paper
            sx={{
              mt: 3,
              p: { xs: 2, md: 3 },
              borderRadius: 2,
              boxShadow:
                "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.4,
              }}
            >
              <CustomTypography variant="heading3">Vouchers</CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                View and manage vouchers. Search by voucher number or product
                name.
              </CustomTypography>
            </Box>
          </Paper>
          <PermissionBlockLabel />
        </Box>
      )}
      {userStore.hasPermission([EmployeeRoles.VIEW_VOUCHER_REQUESTS]) && (
        <Paper
          sx={{
            mt: 3,
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            boxShadow:
              "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.5,
              }}
            >
              <CustomTypography variant="heading3">
                Vouchers Requests
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                View and manage vouchers. Search by voucher number or product
                name.
              </CustomTypography>
            </Box>
          </Box>

          {isLoading && (
            <Container
              sx={{
                minHeight: "40vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Container>
          )}

          {!isLoading && (
            <Box mt={3}>
              {voucherRequestsStore.pullVouchersRequests.length === 0 && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="10rem"
                  textAlign="center"
                  border="1px dashed"
                  borderColor="divider"
                  borderRadius={2}
                >
                  <CustomTypography
                    variant="heading6"
                    fontWeight={500}
                    color={theme.palette.customColors?.greyTextColor}
                  >
                    No vouchers match the current search criteria.
                  </CustomTypography>
                </Box>
              )}
              {voucherRequestsStore.pullVouchersRequests.length > 0 && (
                <VouchersRequestsTable
                  maxHeight={window.innerHeight - 350}
                  pullVouchersRequests={
                    voucherRequestsStore.pullVouchersRequests
                  }
                  limit={voucherRequestsStore.limit}
                  page_no={1}
                  total_count={voucherRequestsStore.total_count}
                  total_pages={voucherRequestsStore.total_pages}
                  sortBy="created_at"
                  sortType={SortType.DESC}
                  onPageChange={() => {}}
                  onRowsPerPageChange={() => {}}
                  onSortChange={() => {}}
                  onCheckStatus={handleCheckStatus}
                />
              )}
            </Box>
          )}
        </Paper>
      )}
    </Grid2>
  );
};

export default observer(VouchersRequests);
